<script setup>
import PdfModal from "@/app/components/modal/PdfModal.vue"
import ActiveBlock from "@/app/components/MyBets/CasinoBonusSection/ActiveBlock.vue"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import { useBonuses } from "@/app/composables/useBonus"
import { useConfig } from "@/app/composables/useConfig"
import { useCurrencies } from "@/app/composables/useCurrencies"
import { t } from "@/app/composables/useI18n"
import { useTranslatedPromoBanner } from "@/casino/composables/useBannerTranslation"
import moment from "moment"
import { computed, ref } from "vue"
import { useRouter } from "vue-router"

const props = defineProps({
  bonus: {},
})
const emit = defineEmits(["close"])

const { files } = useConfig()

const { activeBonus } = useBonuses()
const { getIconByTicker } = useCurrencies()

const bonusTerms = computed(() => {
  return files.value.find(f => f.alias === "bonus-terms" && f.url)
})

const router = useRouter()

const showPdfModal = ref(false)
const pdfLink = ref("")

const bonusRolloverProgress = computed(() => {
  return Math.round((props.bonus?.currentBonusRollover * 100) / props.bonus?.maxBonusRollover)
})

const realRolloverProgress = computed(() => {
  return Math.round(
    (props.bonus?.currentRealFundsRollover * 100) / props.bonus?.maxRealFundsRollover,
  )
})

function handlePdf(link) {
  showPdfModal.value = true
  pdfLink.value = link
}

function onDepositNow() {
  router.push({ query: { action: "open-deposit" } })
  emit("close", false)
}

function getActiveBonusExpireDate(bonus) {
  if (bonus?.expirationDate !== null && bonus?.expirationDate !== "") {
    return `${t("casino.expired_at")} ${moment(bonus?.expirationDate).format(
      "HH:mm DD/MM/YYYY",
    )}`
  }
  else {
    return null
  }
}

const { promoBanner } = useTranslatedPromoBanner()
</script>

<template>
  <div class="status">
    <div v-if="bonus?.maxBonusRollover > 0" class="status-label">
      {{ t("bonus.bonus_rollover") }}
      {{ Math.round(bonus?.maxBonusRollover / bonus?.initialAmount) }}x ({{
        bonus?.maxBonusRollover
      }}
      {{ bonus?.currency?.ticker }})
    </div>
    <div v-if="bonus?.maxBonusRollover > 0">
      <div id="app">
        <div class="shell">
          <div class="bar" :style="{ width: `${bonusRolloverProgress}%` }">
            <span>{{ bonusRolloverProgress }}%</span>
          </div>
        </div>
      </div>
    </div>
    <img v-if="!bonus && promoBanner" :src="promoBanner?.image?.url" alt="">
    <div v-if="!bonus && promoBanner">
      <div class="promo-description" v-html="promoBanner?.description" />
    </div>
    <div v-if="!bonus && !promoBanner">
      <div class="free-bet">
        <div class="free-award">
          {{ t("general.active_bonus_desc") }}
        </div>
      </div>
    </div>
    <div v-if="bonus?.maxRealFundsRollover > 0" class="status-label">
      {{ t("bonus.deposit_rollover") }}
      {{ Math.round(bonus?.maxRealFundsRollover / bonus?.initialAmount) }}x ({{
        bonus?.maxRealFundsRollover
      }}
      {{ bonus?.currency?.ticker }})
    </div>
    <div v-if="bonus?.maxRealFundsRollover > 0" id="app">
      <div class="shell">
        <div class="bar" :style="{ width: `${realRolloverProgress}%` }">
          <span>{{ realRolloverProgress }}%</span>
        </div>
      </div>
    </div>
    <div v-if="bonus" class="status-description">
      {{ t("bonus.short_rules_description") }}
    </div>
    <a
      v-if="bonusTerms"
      class="rules"
      @click="handlePdf(bonusTerms.url)"
    >
      {{ t("bonus.rules_link_title") }}
    </a>
  </div>
  <PdfModal
    :show="showPdfModal"
    :link="pdfLink"
    @close="() => (showPdfModal = false)"
  />
  <ActiveBlock
    v-if="activeBonus !== null"
    class="block"
    :bonus="activeBonus"
    :title="activeBonus.name"
    :expire="getActiveBonusExpireDate(activeBonus)"
    :value="`${activeBonus?.initialAmount} ${activeBonus?.currency?.ticker}`"
    :icon="getIconByTicker(activeBonus?.currency?.ticker)"
  />
  <div>
    <BaseButton class="deposit-now" @click="onDepositNow">
      {{ t("bonus.deposit_now") }}
    </BaseButton>
  </div>
</template>

<style lang="scss" scoped>
.free-award {
  text-align: center;
}

.status {
  margin-top: 16px;
  .status-label {
    font-family: Poppins-reg;
    font-size: 16px;
    font-style: normal;
    margin-bottom: 5px;
  }

  .status-description {
    font-family: Poppins-reg;
    font-size: 16px;
    font-style: normal;
    margin: 20px 0;
  }
}

#app {
  margin-bottom: 16px;
}

.shell {
  height: 34px;
  width: 100%;
  // border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px;
}

.bar {
  background: linear-gradient(90deg, #3886ec 0%, #544fe5 100%);

  height: 26px;
  width: 15px;
  border-radius: 100px;
  span {
    float: right;
    padding: 4px 5px;
    color: #fff;
    font-size: 0.7em;
  }
}

.rules {
  color: #3886ec;
  background-clip: text;
  font-family: Poppins-reg;
  font-size: 16px;
  font-style: normal;
  cursor: pointer;
  line-height: normal;
  text-decoration-line: underline;
}

.deposit-now {
  background: var(--be-primary-btn);
  height: 45px;
  width: calc(100% - 32px);
  border-radius: 8px;
  margin: 32px 16px;
  color: var(--be-primary-btn-label);

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}
</style>
